// Double or Devoured (docs/04 §17.2): guess Higher or Lower on cards 1–13 to climb the ladder
// ×2 → ×3 → ×5 → ×10 → ×25 → ×100. A tie is a push; a wrong guess is DEVOURED (the stake is lost,
// and the machine is a little less hungry for it). The shoe also holds MAW cards, more of them the
// higher you climb: a MAW card devours whatever you guessed. The next rank card is drawn in advance
// so the Visor can hint at it; the Maw's own cards are drawn at the guess.
import { B } from '../../content/balance';
import { D, type Decimal } from '../decimal';
import type { Modifiers } from '../modifiers';
import type { Rng } from '../rng';
import type { HiloBonus } from '../types';

const card = (rng: Rng): number => rng.range(1, 13);

export function createHilo(rng: Rng, par: Decimal, base: Decimal, source: HiloBonus['source']): HiloBonus {
  return {
    mode: 'hilo',
    par,
    base: base.floor(),
    source,
    rung: 0,
    card: card(rng),
    next: card(rng),
    history: [],
    safeUsed: false,
    devoured: false,
    done: false,
    payout: D(0),
    voided: false,
  };
}

export function hiloValue(b: HiloBonus): Decimal {
  if (b.devoured) return D(0);
  const x = b.rung > 0 ? B.HILO_LADDER[b.rung - 1]! : 1;
  return b.base.mul(x).floor();
}

export type HiloOutcome = 'up' | 'push' | 'safe' | 'devoured' | 'top';

/** Chance the next draw is a MAW card on this rung. */
export function mawChance(rung: number): number {
  const m = B.HILO_MAW[Math.min(rung, B.HILO_MAW.length - 1)] ?? 0;
  return m / (13 + m);
}

/** Resolve a guess. `onTop` fires when the Devil's Rung is reached (Plug part #2 on the good path). */
export function hiloGuess(b: HiloBonus, guess: 'hi' | 'lo', mods: Modifiers, rng: Rng): HiloOutcome {
  if (b.done) return 'push';
  b.maw = rng.chance(mawChance(b.rung));
  if (b.maw) {
    if (mods.hiloSafeRung && b.rung === 0 && !b.safeUsed) {
      b.safeUsed = true;
      return 'safe';
    }
    b.devoured = true;
    b.voided = true;
    b.payout = D(0);
    b.done = true;
    return 'devoured';
  }
  const drawn = b.next;
  b.history = [...b.history, b.card].slice(-8);
  const tie = drawn === b.card;
  const right = guess === 'hi' ? drawn > b.card : drawn < b.card;
  b.card = drawn;
  b.next = card(rng);
  if (tie && !mods.hiloTieWins) return 'push';
  if (right || tie) {
    b.rung++;
    if (b.rung >= B.HILO_LADDER.length) {
      b.payout = hiloValue(b);
      b.done = true;
      return 'top';
    }
    return 'up';
  }
  if (mods.hiloSafeRung && b.rung === 0 && !b.safeUsed) {
    b.safeUsed = true;
    return 'safe';
  }
  b.devoured = true;
  b.voided = true;
  b.payout = D(0);
  b.done = true;
  return 'devoured';
}

export function hiloCollect(b: HiloBonus): void {
  if (b.done) return;
  b.payout = hiloValue(b);
  b.done = true;
}

/** Average-skill auto play: guess the likelier side, collect at ×5 (the average bot's habit). */
export function hiloAuto(b: HiloBonus, mods: Modifiers, rng: Rng, stopAt = 3): HiloOutcome[] {
  const out: HiloOutcome[] = [];
  while (!b.done && b.rung < stopAt) out.push(hiloGuess(b, b.card <= B.HILO_HIGH - 1 ? 'hi' : 'lo', mods, rng));
  if (!b.done) hiloCollect(b);
  return out;
}
